import { ChevronDown, SearchMd } from '@timelyapp/icons/line-icons';
import { Button, DropdownMenu, RadixTooltip, TextInput } from '@timelyapp/react';
import React, { useRef, useState } from 'react';
import styles from './AgentTrackingControls.module.scss';
import {
  type AgentProjectStatus,
  type AgentSessionFilters,
  EMPTY_SESSION_FILTERS,
  PROJECT_STATUS_OPTIONS,
} from './agentTracking.model';

export default function AgentTrackingControls({
  filters,
  onChange,
  children,
  statusCounts,
}: {
  filters: AgentSessionFilters;
  onChange: (filters: AgentSessionFilters) => void;
  children?: React.ReactNode;
  statusCounts?: Record<AgentProjectStatus, number>;
}) {
  const [searchOpen, setSearchOpen] = useState(!!filters.query);
  const restoreFocus = useRef(false);
  const closeSearch = () => {
    onChange({ ...filters, query: '' });
    restoreFocus.current = true;
    setSearchOpen(false);
  };
  const status = filters.projectStatus ?? 'all';
  const hasFilters = !!(filters.query || filters.provider || filters.project || status !== 'all');
  return (
    <div className={styles.controls} role="search" aria-label="Filter agent sessions">
      {statusCounts && (
        <div className={styles.statusFilter}>
          <DropdownMenu
            align="end"
            sideOffset={4}
            closeOnSelect
            trigger={
              <Button size="small" priority="medium" aria-label="Project status">
                {status === 'all'
                  ? 'Project status'
                  : `Project: ${PROJECT_STATUS_OPTIONS.find(option => option.value === status)?.label}`}
                <ChevronDown />
              </Button>
            }
            items={[
              {
                type: 'radioGroup',
                label: 'Project status',
                currentValue: status,
                onValueChange: value =>
                  onChange({ ...filters, projectStatus: value as AgentProjectStatus }),
                subOptions: PROJECT_STATUS_OPTIONS.map(option => ({
                  type: 'radioItem',
                  value: option.value,
                  label: `${option.label} (${statusCounts[option.value]})`,
                })),
              },
            ]}
          />
        </div>
      )}
      {children}
      {searchOpen || filters.query ? (
        <TextInput
          className={styles.searchField}
          size="small"
          type="search"
          aria-label="Search sessions"
          placeholder="Search sessions…"
          prefix={<SearchMd className={styles.searchIcon} />}
          value={filters.query}
          autoFocus
          onChange={event => onChange({ ...filters, query: event.target.value })}
          onClear={closeSearch}
          onKeyDown={event => {
            if (event.key === 'Escape') {
              event.preventDefault();
              closeSearch();
            }
          }}
          onBlur={() => {
            if (!filters.query) setSearchOpen(false);
          }}
        />
      ) : (
        <RadixTooltip tooltip="Search sessions" delayDuration={0}>
          <Button
            size="small"
            priority="medium"
            aria-label="Search sessions"
            onClick={() => setSearchOpen(true)}
            ref={node => {
              if (node && restoreFocus.current) {
                node.focus();
                restoreFocus.current = false;
              }
            }}
          >
            <SearchMd className={styles.searchIcon} />
            <span className={styles.searchLabel}>Search</span>
          </Button>
        </RadixTooltip>
      )}
      {hasFilters && (
        <Button
          size="small"
          priority="low"
          onClick={() => {
            restoreFocus.current = true;
            setSearchOpen(false);
            onChange(EMPTY_SESSION_FILTERS);
          }}
        >
          {statusCounts ? 'Reset filters' : 'Clear search'}
        </Button>
      )}
    </div>
  );
}
